import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializePropertyCostBase } from '../utils/propertyMapper';
import { validateProperty, normalizeProperty, isPropertyPPOR } from '../utils/propertyUtils';
import { normalizeInvestmentGoal } from '../utils/simulationHelpers';
import { track } from '../utils/analytics';
import {
  DEFAULT_ASSUMPTIONS as CONFIG_DEFAULTS,
  HOLDING_COST_DEFAULTS,
} from '../config/portfolioAssumptions';
import { DEFAULT_PORTFOLIO_STRATEGY } from '../config/portfolioDefaults';
import investorProfileAPI from '../services/investorProfileApi';
import { useAuth } from '../components/auth/AuthWrapper';
// Phase 5b: validate setInvestorProfile writes against the canonical zod schema.
// Warning-only — Phase 5f flips to throw in dev once consumers are clean.
import { safeParseInvestorProfile } from '../schema/investorProfile.zod';
// Regex-only format check for the watchlist auto-repair below — deliberately
// NOT the repair module itself, which drags in the 485KB postcode table and
// is dynamic-imported only when an invalid entry actually exists.
import { isValidPreferredFormat } from '../utils/preferredSuburbFormat';

const STORAGE_KEY_PREFIX = 'heyward_portfolio_data'; // namespaced per-user: prefix_userId
const STORAGE_VERSION = 7; // v7: + age + stateOfResidence (ADR-015 segment policies — pure additive over v6)

// Returns the user-scoped localStorage key so different users on the same
// browser never read each other's cached portfolio data.
const storageKey = (userId) =>
  userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX;

// ─── Write-through cache helpers ────────────────────────────────────────────
// localStorage is used as an instant-load cache ONLY.
// Supabase is still the source of truth — backend data always overwrites cache.
// Cache survives refresh so users never see a blank portfolio while the
// backend fetch is in flight.
const writeCache = (userId, { properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, lastModified }) => {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({
      version: STORAGE_VERSION,
      cachedAt: new Date().toISOString(),
      lastModified: lastModified ?? null,
      properties,
      assumptions,
      investorProfile,
      investmentGoal,
      portfolioStrategy,
    }));
  } catch (e) { /* storage full — ignore */ }
};

const readCache = (userId) => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch (e) { return null; }
};

/**
 * PortfolioDataContext - Manages portfolio data with localStorage persistence
 * Provides seamless data flow across all portfolio components
 */
const PortfolioDataContext = createContext(null);

export const usePortfolioData = () => {
  const context = useContext(PortfolioDataContext);
  if (!context) {
    throw new Error('usePortfolioData must be used within a PortfolioDataProvider');
  }
  return context;
};

/**
 * Complete assumptions object with all required fields
 * Sourced from centralized config/portfolioAssumptions.js
 */
const DEFAULT_ASSUMPTIONS = {
  // Core rates
  interestRate: CONFIG_DEFAULTS.interestRate,
  loanTermYears: CONFIG_DEFAULTS.loanTermYears,
  loanTermMonths: CONFIG_DEFAULTS.loanTermMonths,

  // Growth rates
  capitalGrowthRate: CONFIG_DEFAULTS.capitalGrowthRate,
  rentalGrowthRate: CONFIG_DEFAULTS.rentalGrowthRate,
  inflationRate: CONFIG_DEFAULTS.inflationRate,

  // Vacancy
  vacancyWeeks: CONFIG_DEFAULTS.vacancyWeeks,
  occupancyRate: CONFIG_DEFAULTS.occupancyRate,

  // Expense ratios
  expenseRatioPercent: CONFIG_DEFAULTS.expenseRatioPercent,
  propertyManagementFeePercent: CONFIG_DEFAULTS.propertyManagementFeePercent,

  // Maintenance
  maintenancePercentOfValue: CONFIG_DEFAULTS.maintenancePercentOfValue,

  // Tax
  marginalTaxRate: CONFIG_DEFAULTS.marginalTaxRate,

  // Holding cost defaults (can be overridden per property)
  councilRatesPA: HOLDING_COST_DEFAULTS.councilRatesPA,
  waterRatesPA: HOLDING_COST_DEFAULTS.waterRatesPA,
  insurancePA: HOLDING_COST_DEFAULTS.insurancePA,
};

/**
 * Canonical investor profile schema (v6).
 *
 * This is the SINGLE SOURCE OF TRUTH for investor data across Heyward.
 * Every consumer (BorrowingCalculator, AI Advisor, Scenarios, Settings)
 * reads from this shape.
 *
 * Only fields with live consumers are included. Backend `profile_data` and
 * `preferences_data` JSONB columns may carry additional fields that pass
 * through unchanged (preferredAssetClasses, liquidityRequirements, etc).
 */
const DEFAULT_INVESTOR_PROFILE = {
  // Identity
  name: null,
  email: null,

  // Entity (Individual / Joint / SMSF / Trust / Corporate)
  investmentEntity: {
    type: null,        // 'Individual' | 'Joint' | 'SMSF' | 'Trust' | 'Corporate'
    name: null,
    abn: null,
    trusteeType: null, // 'Individual' | 'Corporate' (for SMSF/Trust only)
  },

  // Risk + horizon + calibration (v7)
  riskProfile: null,        // 'Conservative' | 'Balanced' | 'Growth' | 'Aggressive'
  investmentHorizon: null,  // '2-5 years' | '5-10 years' | '10+ years'
  age: null,                // 18-100; required by engine for ADR-015 segment policies (equity release thresholds)
  stateOfResidence: null,   // 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT'; used for stamp duty + equity release policy

  // Household income (replaces hardcoded mock employment numbers)
  householdIncome: {
    primaryApplicant: {
      payg: 0,
      selfEmployed: 0,
      overtimeBonusCommission: 0,
      investment: 0,        // dividends + interest
      other: 0,
    },
    secondaryApplicant: null, // null when single, populated when couple
    status: 'single',          // 'single' | 'couple'
    dependants: 0,
  },

  // Household expenses (used by BorrowingCalculator serviceability calc)
  householdExpenses: {
    useHEM: true,              // auto-calc from Household Expenditure Method
    livingExpensesMonthly: 0,
    creditCardLimits: 0,
    existingHomeLoanMonthly: 0,
    otherLoanMonthly: 0,
  },

  // Tax position
  taxPosition: {
    marginalTaxRate: 0.37,     // mirrors assumptions.marginalTaxRate
    cgtDiscountEligible: true,
  },

  // Liquid assets (used by BorrowingCalculator deposit slot + investmentRecommendations)
  assets: {
    cash: 0,                   // cash + offset accounts available for next deposit
  },

  // Geographic preferences (used by AI Advisor dynamic suggestions, fit score)
  geographicPreferences: {
    states: [],                // ['NSW', 'VIC', 'QLD']
    capitalCities: true,
    regionalAreas: false,
    preferredSuburbs: [],
    excludedAreas: [],
  },

  propertyFeatures: {},

  // Pain points — captured on cold HomePage (P1 #1 from onboarding-first-session audit).
  // Array of stable IDs from PainPointsCapture's PAIN_OPTIONS list. Agent voice reads
  // these in downstream flows to mirror the user's stated friction.
  painPoints: [],
};

/**
 * Normalize a possibly-raw investor profile into the canonical v6 shape.
 * Handles three input shapes:
 *   1. v5 localStorage (whatever shape was set by setInvestorProfile previously)
 *   2. Backend API response (profile_data + portfolio_data + preferences_data + entity_type + risk_profile)
 *   3. Sarah Chen mock (currentPortfolio.totalValue, etc.)
 *
 * Always returns a complete profile with all canonical fields, falling back
 * to DEFAULT_INVESTOR_PROFILE values for any missing slots. Preserves any
 * unknown fields via spread so backend extensions don't get dropped.
 */
const normalizeInvestorProfile = (raw) => {
  if (!raw) return { ...DEFAULT_INVESTOR_PROFILE };

  // Backend API shape: { entity_type, risk_profile, profile_data: {...}, preferences_data: {...} }
  const profileData = raw.profile_data || raw;
  const prefsData = raw.preferences_data || profileData;

  return {
    ...DEFAULT_INVESTOR_PROFILE,
    ...profileData,
    name: profileData.name || raw.name || DEFAULT_INVESTOR_PROFILE.name,
    email: profileData.email || raw.email || DEFAULT_INVESTOR_PROFILE.email,

    investmentEntity: {
      ...DEFAULT_INVESTOR_PROFILE.investmentEntity,
      ...(profileData.investmentEntity || {}),
      type:
        profileData.investmentEntity?.type ||
        raw.entity_type ||
        DEFAULT_INVESTOR_PROFILE.investmentEntity.type,
    },

    riskProfile:
      profileData.riskProfile ||
      raw.risk_profile ||
      DEFAULT_INVESTOR_PROFILE.riskProfile,

    investmentHorizon:
      profileData.investmentHorizon || DEFAULT_INVESTOR_PROFILE.investmentHorizon,

    age: profileData.age ?? DEFAULT_INVESTOR_PROFILE.age,

    stateOfResidence: profileData.stateOfResidence ?? DEFAULT_INVESTOR_PROFILE.stateOfResidence,

    householdIncome: {
      ...DEFAULT_INVESTOR_PROFILE.householdIncome,
      ...(profileData.householdIncome || {}),
      primaryApplicant: {
        ...DEFAULT_INVESTOR_PROFILE.householdIncome.primaryApplicant,
        ...(profileData.householdIncome?.primaryApplicant || {}),
      },
      secondaryApplicant: profileData.householdIncome?.secondaryApplicant
        ? {
            ...DEFAULT_INVESTOR_PROFILE.householdIncome.primaryApplicant,
            ...profileData.householdIncome.secondaryApplicant,
          }
        : null,
    },

    householdExpenses: {
      ...DEFAULT_INVESTOR_PROFILE.householdExpenses,
      ...(profileData.householdExpenses || {}),
    },

    taxPosition: {
      ...DEFAULT_INVESTOR_PROFILE.taxPosition,
      ...(profileData.taxPosition || {}),
    },

    assets: {
      ...DEFAULT_INVESTOR_PROFILE.assets,
      ...(profileData.assets || {}),
    },

    geographicPreferences: {
      ...DEFAULT_INVESTOR_PROFILE.geographicPreferences,
      ...(prefsData.geographicPreferences || profileData.geographicPreferences || {}),
    },

    // An explicit empty preferences object clears older profile-data rules.
    propertyFeatures: prefsData.propertyFeatures ?? profileData.propertyFeatures ?? {},

    painPoints: Array.isArray(profileData.painPoints)
      ? profileData.painPoints
      : DEFAULT_INVESTOR_PROFILE.painPoints,
  };
};

/**
 * Detect whether a profile has any meaningful (non-default) data.
 * Used to guard auto-sync from overwriting populated backend with empty defaults.
 */
const profileIsMeaningful = (profile) => {
  if (!profile) return false;
  if (profile.name) return true;
  if (profile.email) return true;
  if (profile.investmentEntity?.type) return true;
  if (profile.riskProfile) return true;
  if (profile.householdIncome?.primaryApplicant?.payg > 0) return true;
  if (profile.householdExpenses?.livingExpensesMonthly > 0) return true;
  if (profile.geographicPreferences?.states?.length > 0) return true;
  if (Object.keys(profile.propertyFeatures || {}).length > 0) return true;
  if (Array.isArray(profile.painPoints) && profile.painPoints.length > 0) return true;
  // First-visit slider outputs — a completed (even fully skipped) intake is
  // meaningful state that must reach the cloud, or a second device re-runs
  // onboarding from scratch.
  if (profile.firstSessionGoal) return true;
  if (profile.budget > 0) return true;
  if (profile.onboardingComplete === true) return true;
  return false;
};

const GOAL_STORAGE_KEY = 'heyward_investment_goal'; // Legacy key for migration
const ONBOARDING_STORAGE_KEY = 'heyward_onboarding_progress'; // Progressive onboarding progress
const STRATEGY_STORAGE_KEY = 'heyward_strategy_progress'; // Strategy journey progress (save/resume)

/**
 * Load data from localStorage.
 * Only loads non-sensitive resume data (onboarding/strategy progress).
 * Portfolio data, investor profile, and properties are loaded from backend only.
 * @deprecated Portfolio data loading removed — backend is single source of truth.
 */
const loadFromStorage = (_userId) => {
  // Portfolio data no longer loaded from localStorage.
  // Return null so the backend fetch runs as the primary load.
  return null;
  // eslint-disable-next-line no-unreachable
  try {
    const stored = localStorage.getItem(storageKey(_userId));
    const legacyGoal = localStorage.getItem(GOAL_STORAGE_KEY);
    let parsedLegacyGoal = null;
    if (legacyGoal) {
      try {
        parsedLegacyGoal = JSON.parse(legacyGoal);
      } catch (e) {
        console.warn('Failed to parse legacy goal:', e);
      }
    }

    if (stored) {
      const parsed = JSON.parse(stored);

      // Handle version migration from v1
      if (parsed.version === 1) {
        console.info('Migrating portfolio data from v1 to v4');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: parsed.investorProfile || null,
          investmentGoal: parsedLegacyGoal || null,
          lastModified: parsed.lastUpdated || new Date().toISOString(),
          lastCloudSave: null,
        };
      }

      // Handle version migration from v2
      if (parsed.version === 2) {
        console.info('Migrating portfolio data from v2 to v4');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: parsed.investorProfile || null,
          investmentGoal: parsedLegacyGoal || null,
          lastModified: parsed.lastUpdated || new Date().toISOString(),
          lastCloudSave: null,
        };
      }

      // Handle version migration from v3 (add investmentGoal)
      if (parsed.version === 3) {
        console.info('Migrating portfolio data from v3 to v4');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: parsed.investorProfile || null,
          investmentGoal: parsed.investmentGoal || parsedLegacyGoal || null,
          lastModified: parsed.lastModified || parsed.lastUpdated || null,
          lastCloudSave: parsed.lastCloudSave || null,
        };
      }

      // Handle version migration from v4 (add portfolioStrategy)
      if (parsed.version === 4) {
        console.info('Migrating portfolio data from v4 to v6');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: normalizeInvestorProfile(parsed.investorProfile),
          investmentGoal: parsed.investmentGoal || null,
          portfolioStrategy: DEFAULT_PORTFOLIO_STRATEGY,
          lastModified: parsed.lastModified || parsed.lastUpdated || null,
          lastCloudSave: parsed.lastCloudSave || null,
        };
      }

      // Handle version migration from v5 (canonical investor profile schema)
      if (parsed.version === 5) {
        console.info('Migrating portfolio data from v5 to v7 — normalizing investor profile, adding age + stateOfResidence');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: normalizeInvestorProfile(parsed.investorProfile),
          investmentGoal: parsed.investmentGoal || null,
          portfolioStrategy: parsed.portfolioStrategy || DEFAULT_PORTFOLIO_STRATEGY,
          lastModified: parsed.lastModified || parsed.lastUpdated || null,
          lastCloudSave: parsed.lastCloudSave || null,
        };
      }

      // Handle version migration from v6 → v7 (add age + stateOfResidence; pure additive)
      if (parsed.version === 6) {
        console.info('Migrating portfolio data from v6 to v7 — adding age + stateOfResidence');
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: normalizeInvestorProfile(parsed.investorProfile),
          investmentGoal: parsed.investmentGoal || null,
          portfolioStrategy: parsed.portfolioStrategy || DEFAULT_PORTFOLIO_STRATEGY,
          lastModified: parsed.lastModified || parsed.lastUpdated || null,
          lastCloudSave: parsed.lastCloudSave || null,
        };
      }

      if (parsed.version === STORAGE_VERSION) {
        return {
          properties: parsed.properties || [],
          assumptions: { ...DEFAULT_ASSUMPTIONS, ...parsed.assumptions },
          investorProfile: normalizeInvestorProfile(parsed.investorProfile),
          investmentGoal: parsed.investmentGoal || null,
          portfolioStrategy: parsed.portfolioStrategy || DEFAULT_PORTFOLIO_STRATEGY,
          lastModified: parsed.lastModified || parsed.lastUpdated || null,
          lastCloudSave: parsed.lastCloudSave || null,
        };
      }
    }

    // No user-scoped data — check legacy unscoped key for properties to migrate.
    // This happens when the user added properties before user-scoped keys were
    // introduced (STORAGE_KEY_PREFIX without userId suffix).
    const legacyUnscoped = localStorage.getItem(STORAGE_KEY_PREFIX);
    if (legacyUnscoped) {
      try {
        const legacyParsed = JSON.parse(legacyUnscoped);
        const legacyProperties = (legacyParsed.properties || []).filter(
          p => p.address || p.fullAddress // only migrate properties with a real address
        );
        if (legacyProperties.length > 0) {
          console.info(`[PortfolioDataContext] Migrating ${legacyProperties.length} properties from legacy unscoped key`);
          // Remove the legacy key so this migration only runs once
          localStorage.removeItem(STORAGE_KEY_PREFIX);
          return {
            properties: legacyProperties,
            assumptions: { ...DEFAULT_ASSUMPTIONS, ...(legacyParsed.assumptions || {}) },
            investorProfile: legacyParsed.investorProfile || null,
            investmentGoal: legacyParsed.investmentGoal || parsedLegacyGoal || null,
            portfolioStrategy: legacyParsed.portfolioStrategy || DEFAULT_PORTFOLIO_STRATEGY,
            lastModified: legacyParsed.lastModified || null,
            lastCloudSave: null,
          };
        }
      } catch (e) {
        console.warn('[PortfolioDataContext] Failed to parse legacy unscoped key:', e);
      }
    }

    // No stored data but legacy goal exists
    if (parsedLegacyGoal) {
      return {
        properties: [],
        assumptions: DEFAULT_ASSUMPTIONS,
        investorProfile: null,
        investmentGoal: parsedLegacyGoal,
        portfolioStrategy: DEFAULT_PORTFOLIO_STRATEGY,
        lastModified: null,
        lastCloudSave: null,
      };
    }
  } catch (error) {
    console.error('Failed to load portfolio data from localStorage:', error);
  }
  return null;
};

/**
 * Save data to localStorage under the user-scoped key.
 */
const saveToStorage = (userId, data) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({
      version: STORAGE_VERSION,
      ...data,
    }));
  } catch (error) {
    console.error('Failed to save portfolio data to localStorage:', error);
  }
};

export const PortfolioDataProvider = ({ children }) => {
  // Auth — needed for auto-sync to know whose profile to push to backend
  const { user } = useAuth();
  const userId = user?.userId;  // undefined during Clerk loading window — callers must guard

  // Core state
  const [properties, setProperties] = useState([]);
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  const [investorProfile, setInvestorProfileState] = useState(null);
  const [investmentGoal, setInvestmentGoalState] = useState(null);
  const [portfolioStrategy, setPortfolioStrategyState] = useState(DEFAULT_PORTFOLIO_STRATEGY);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isProfileReconciled, setIsProfileReconciled] = useState(false);

  // Progressive onboarding state
  const [onboardingProgress, setOnboardingProgressState] = useState(null);

  // Strategy journey progress (for save/resume)
  const [strategyProgress, setStrategyProgressState] = useState(null);

  // Cloud sync state
  const [lastModified, setLastModified] = useState(null);
  const [lastCloudSave, setLastCloudSave] = useState(null);
  const [cloudSaveStatus, setCloudSaveStatus] = useState('idle'); // idle | saving | syncing | synced | saved | error
  const [cloudSaveError, setCloudSaveError] = useState(null);
  const lastModifiedRef = useRef(null);
  const cloudSaveTimeoutRef = useRef(null);

  // Phase 1 auto-sync infrastructure
  const reconcileCompleteRef = useRef(false);  // guards auto-sync during hydration
  const autoSyncTimeoutRef = useRef(null);     // debounce timer for auto-sync
  const autoSyncAbortRef = useRef(null);       // AbortController for in-flight saves
  const skipNextAutoSyncRef = useRef(false);   // set after reconcile-from-cloud to avoid echo
  const retryCountRef = useRef(0);             // exponential-backoff retry counter (resets on success)
  const retryTimerRef = useRef(null);          // pending backoff retry handle
  const lastSyncFailureStatusRef = useRef(null); // status that opened the current failure episode (rides sync_recovered)
  // Ref to break the circular dep between _syncToCloudSilent and scheduleRetry
  // (scheduleRetry needs to call the latest _syncToCloudSilent, but is itself
  // called from inside _syncToCloudSilent's error path)
  const syncFnRef = useRef(null);

  // Backoff schedule: 3s, 9s, 27s — three attempts then surface manual retry
  const RETRY_DELAYS_MS = useMemo(() => [3000, 9000, 27000], []);

  // ========================================================================
  // Backend-first load: Supabase is the single source of truth.
  // localStorage is NOT used for portfolio/profile data — only for
  // non-sensitive resume data (onboarding/strategy wizard progress).
  // Shows loading state (~300-500ms) then hydrates from backend.
  // ========================================================================
  useEffect(() => {
    if (userId === undefined) return; // Clerk still loading

    // Reset reconcile flag for this user session
    reconcileCompleteRef.current = false;
    setIsProfileReconciled(false);

    // Reset state for clean load (handles account switching)
    setProperties([]);
    setInvestorProfileState(null);
    setInvestmentGoalState(null);
    setAssumptions(DEFAULT_ASSUMPTIONS);
    setPortfolioStrategyState(DEFAULT_PORTFOLIO_STRATEGY);
    setIsLoaded(false);

    // Load only non-sensitive resume data from localStorage
    try {
      const savedOnboarding = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (savedOnboarding) setOnboardingProgressState(JSON.parse(savedOnboarding));
    } catch (e) {
      console.warn('Failed to load onboarding progress:', e);
    }
    try {
      const savedStrategy = localStorage.getItem(STRATEGY_STORAGE_KEY);
      if (savedStrategy) setStrategyProgressState(JSON.parse(savedStrategy));
    } catch (e) {
      console.warn('Failed to load strategy progress:', e);
    }

    // No userId (logged out) — mark loaded with empty state
    if (!userId) {
      setIsLoaded(true);
      setIsProfileReconciled(true);
      return;
    }

    // ── Step 1: Load cache instantly so UI shows data immediately ──────────
    const cache = readCache(userId);
    if (cache) {
      if (Array.isArray(cache.properties) && cache.properties.length > 0) {
        console.log('[loadFromCache] restoring', cache.properties.length, 'properties, lastModified:', cache.lastModified);
        setProperties(cache.properties);
      }
      if (cache.investorProfile) setInvestorProfileState(cache.investorProfile);
      if (cache.investmentGoal) setInvestmentGoalState(cache.investmentGoal);
      if (cache.assumptions) setAssumptions({ ...DEFAULT_ASSUMPTIONS, ...cache.assumptions });
      if (cache.portfolioStrategy) setPortfolioStrategyState(cache.portfolioStrategy);
      // Show cached data immediately (no spinner) but do NOT set reconcileCompleteRef
      // here — that gate must only be opened by loadFromBackend. Setting it from cache
      // lets the auto-sync fire 500ms later with stale cache data before the backend
      // response arrives, which re-pushes deleted properties back to the DB.
      setIsLoaded(true);
    }

    // ── Step 2: Fetch from backend and overwrite cache ───────────────────────
    // Backend is the source of truth. Cache is just for instant display.
    let cancelled = false;
    const loadFromBackend = async () => {
      try {
        const result = await investorProfileAPI.getInvestorProfile(userId, { skipMockFallback: true });
        if (cancelled) return;

        const backendData = result?.data;   // transformed — for investorProfile (legacy fields only)
        const rawData = result?.rawData;    // raw backend shape — profile_data + portfolio_data intact
        if (result?.source === 'api' && backendData) {
          // BUG FIX: Use rawData instead of backendData for normalizeInvestorProfile.
          // backendData is the result of _transformApiToProfile() which only maps legacy fields
          // (name, email, entity, risk) and DROPS all v6/v7 canonical fields including
          // householdIncome, householdExpenses, assets, and geographicPreferences.
          // rawData contains the full profile_data JSONB from Postgres, which normalizeInvestorProfile
          // correctly reads via `raw.profile_data` to populate all canonical fields.
          const normalizedProfile = normalizeInvestorProfile(rawData ?? backendData);
          setInvestorProfileState(normalizedProfile);

          // Use rawData.portfolio_data — backendData goes through _transformApiToProfile
          // which renames portfolio_data → currentPortfolio, losing the key entirely.
          // Fallback: some users have properties in profile_data.currentPortfolio (old shape).
          const pd = rawData?.portfolio_data;
          const legacyPd = rawData?.profile_data?.currentPortfolio;

          // Last-writer-wins: if local cache was modified more recently than the
          // backend's updated_at, the user made a change (e.g. a delete) that
          // hasn't synced yet. Don't overwrite local properties with stale backend data.
          const backendUpdatedAt = rawData?.updated_at;
          const cacheLastModified = cache?.lastModified;
          const localIsNewer = cacheLastModified && backendUpdatedAt &&
            new Date(cacheLastModified).getTime() > new Date(backendUpdatedAt).getTime();

          console.log('[loadFromBackend] localIsNewer:', localIsNewer, '| cacheLastModified:', cacheLastModified, '| backendUpdatedAt:', backendUpdatedAt);
          if ((pd || legacyPd) && !localIsNewer) {
            // pd.properties being an explicit array (even []) means the backend owns
            // the properties list — honour it including empty (user deleted all).
            // Only fall back to legacyPd when pd has no properties key at all.
            const backendOwnsProps = Array.isArray(pd?.properties);
            const rawProps = backendOwnsProps
              ? pd.properties
              : (Array.isArray(legacyPd?.properties) && legacyPd.properties.length > 0
                  ? legacyPd.properties
                  : null);

            if (rawProps !== null) {
              // Normalize snake_case API fields → camelCase frontend fields
              const validProperties = rawProps
                // Accept any property with an id or address or suburb — be permissive
                .filter(p => p.id || p.address || p.fullAddress || p.suburb)
                .map(p => ({
                  ...p,
                  value: p.value ?? p.current_value ?? p.currentValue ?? 0,
                  loanAmount: p.loanAmount ?? p.loan_amount ?? 0,
                  weeklyRent: p.weeklyRent ?? p.weekly_rent ?? 0,
                  propertyType: p.propertyType ?? p.property_type,
                  purchasePrice: p.purchasePrice ?? p.purchase_price ?? p.costBase?.purchasePrice ?? 0,
                  purchaseDate: p.purchaseDate ?? p.settlement_date,
                  purchaseYear: p.purchaseYear ?? p.purchase_year ?? (
                    p.purchaseDate || p.settlement_date
                      ? new Date(p.purchaseDate ?? p.settlement_date).getFullYear()
                      : null
                  ),
                  fullAddress: p.fullAddress ?? p.address ?? (p.suburb ? `${p.suburb}${p.state ? ', ' + p.state : ''}` : 'Property'),
                }));
              console.log('[loadFromBackend] setting', validProperties.length, 'properties from backend');
              // Always honour the backend list — including empty (clears stale cache)
              setProperties(validProperties);
            }

            const goal = pd?.investmentGoal || legacyPd?.investmentGoal;
            if (goal) setInvestmentGoalState(goal);
            const assump = pd?.assumptions || legacyPd?.assumptions;
            if (assump) setAssumptions({ ...DEFAULT_ASSUMPTIONS, ...assump });
          }

          const sd = rawData?.strategy_data;
          if (sd && Object.keys(sd).length > 0) {
            setPortfolioStrategyState({ ...DEFAULT_PORTFOLIO_STRATEGY, ...sd });
          }

          const savedAt = rawData?.updated_at || rawData?.lastUpdated;
          if (savedAt) setLastCloudSave(savedAt);
          // Only suppress the next auto-sync when we rehydrated FROM backend.
          // When localIsNewer=true we kept local state (e.g. a pending delete) and
          // must let the auto-sync fire to push that change to backend.
          if (!localIsNewer) {
            skipNextAutoSyncRef.current = true;
          }
        }
      } catch (err) {
        console.warn('[PortfolioDataContext] Backend load failed — using cache:', err.message);
      } finally {
        if (!cancelled) {
          reconcileCompleteRef.current = true;
          setIsProfileReconciled(true);
          setIsLoaded(true);
        }
      }
    };

    loadFromBackend();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // DEBUG: track every properties state change
  useEffect(() => {
    console.log('[PROPS_STATE]', properties.length, 'properties:', properties.map(p => p.id), new Error().stack?.split('\n').slice(1,3).join(' | '));
  }, [properties]);

  // ── Write-through cache: keep localStorage in sync after every state change ──
  // This runs after every meaningful change so the cache is always fresh.
  // Supabase auto-sync also fires (separately) to persist to the backend.
  useEffect(() => {
    if (!isLoaded || !userId) return;
    writeCache(userId, { properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, lastModified });
  }, [userId, isLoaded, properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, lastModified]);

  /**
   * Mark data as modified (called when any data changes).
   *
   * IMPORTANT: This MUST be stable (empty deps). It's consumed by every
   * setter callback in this context (setInvestorProfile, setPortfolioStrategy,
   * addProperty, etc.), and those setters propagate into consumers'
   * useCallback/useEffect dep arrays. If markModified gets a new reference
   * on every cloudSaveStatus change, it cascades into a feedback loop:
   *
   *   sync sets status → markModified ref changes → setters change refs
   *   → consumer callbacks change → consumer effects re-fire → API re-loaded
   *   → state updated → auto-sync re-fires → infinite loop at ~1Hz.
   *
   * Use functional setState to read latest cloudSaveStatus without depending
   * on it as a hook dep. Same pattern for `saved → idle` reset.
   */
  const markModified = useCallback(() => {
    const now = new Date().toISOString();
    setLastModified(now);
    lastModifiedRef.current = now;
    setCloudSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
  }, []);

  /**
   * Check if there are unsaved changes
   */
  const hasUnsavedChanges = useMemo(() => {
    if (!lastModified) return false;
    if (!lastCloudSave) return true; // Never saved to cloud
    return new Date(lastModified) > new Date(lastCloudSave);
  }, [lastModified, lastCloudSave]);

  /**
   * Save current data to cloud (Supabase) for the currently authed user.
   * Uses closure userId from useAuth — caller no longer passes it (IDOR fix).
   * @returns {Promise<{success: boolean, savedAt?: string, error?: string}>}
   */
  const saveToCloud = useCallback(async () => {
    if (!userId) {
      return { success: false, error: 'Not signed in' };
    }
    if (cloudSaveStatus === 'saving') {
      return { success: false, error: 'Save already in progress' };
    }

    setCloudSaveStatus('saving');
    setCloudSaveError(null);

    try {
      // Calculate metrics inline using safe accessors to handle field name variants
      const totalValue = properties.reduce((sum, p) =>
        sum + (p.value ?? p.currentValue ?? p.current_value ?? 0), 0
      );
      const totalDebt = properties.reduce((sum, p) =>
        sum + (p.loanAmount ?? p.loan_amount ?? p.loan ?? 0), 0
      );
      const totalEquity = totalValue - totalDebt;

      // Prepare portfolio data for API with normalized field names
      const portfolioData = {
        properties: properties.map(p => ({
          id: p.id || p._id,
          address: p.address || p.fullAddress,
          suburb: p.suburb,
          state: p.state,
          postcode: p.postcode,
          property_type: p.propertyType || p.property_type || p.type,
          purchase_price: p.purchasePrice ?? p.purchase_price,
          current_value: p.value ?? p.currentValue ?? p.current_value,
          weekly_rent: p.weeklyRent ?? p.weekly_rent ?? p.rent,
          loan_amount: p.loanAmount ?? p.loan_amount ?? p.loan,
          settlement_date: p.purchaseDate ?? p.purchase_date ?? p.settlementDate,
          purchase_year: p.purchaseYear ?? p.purchase_year ?? null,
          // 2026 budget reform — gates NG eligibility + CGT method per property.
          // Sent in both shapes the engine accepts (snake_case is canonical).
          contract_date: p.contract_date ?? p.contractDate ?? null,
          property_classification: p.property_classification ?? p.propertyClassification ?? null,
          // ADR-022 §4.2 — ATO Table 2 new build qualification (factsheet p.6).
          new_build_supply_basis: p.new_build_supply_basis ?? p.newBuildSupplyBasis ?? null,
          prior_ownership: p.prior_ownership ?? p.priorOwnership ?? null,
          // ADR-022 §4.4 — affordable housing 60% CGT discount preserved.
          affordable_housing_qualifying: p.affordable_housing_qualifying ?? p.affordableHousingQualifying ?? null,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          // Preserve extended data
          expenses: p.expenses,
          qsData: p.qsData,
          rentalData: p.rentalData,
          costBase: p.costBase,
          // PPOR flag — same parity-with-auto-sync fix. Without these
          // a manual saveToCloud round-trips the property as
          // 'investment' even after the user toggles HOME in the
          // PropertyEditModal.
          property_category: p.propertyCategory ?? p.property_category ?? 'investment',
          propertyCategory: p.propertyCategory ?? p.property_category ?? 'investment',
          // Per-property ownership entity (2026-08-28) — labeling/display only.
          // No engine consumer reads it; the tax-surface partition is a
          // separate, gated phase. null = not set (treated as personal).
          ownership: p.ownership ?? null,
          // Backend-injected fields — round-trip so they survive saves
          weeklyRent: p.weeklyRent,
          lastStatementDate: p.lastStatementDate,
          annualInsurancePremium: p.annualInsurancePremium,
          insurancePolicyExpiry: p.insurancePolicyExpiry,
          lastInsuranceRenewalDate: p.lastInsuranceRenewalDate,
        })),
        totalValue,
        totalEquity,
        totalDebt,
        propertyCount: properties.length,
        assumptions: assumptions,
        investmentGoal: investmentGoal, // Include goal in cloud save
        lastUpdated: new Date().toISOString(),
      };

      const result = await investorProfileAPI.patchPortfolioData(userId, portfolioData);

      if (result.success) {
        const savedAt = result.savedAt || new Date().toISOString();
        setLastCloudSave(savedAt);
        setCloudSaveStatus('saved');
        // Manual save also clears the auto-retry counter so the next failure
        // starts fresh at 3s instead of jumping to 27s
        retryCountRef.current = 0;
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }

        // Clear any existing timeout to prevent race condition
        if (cloudSaveTimeoutRef.current) {
          clearTimeout(cloudSaveTimeoutRef.current);
        }

        // Auto-reset to idle after 3 seconds
        cloudSaveTimeoutRef.current = setTimeout(() => {
          setCloudSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
          cloudSaveTimeoutRef.current = null;
        }, 3000);

        return { success: true, savedAt };
      } else {
        // API returned but with error
        const errorMessage = result.error || 'Failed to save to cloud';
        setCloudSaveStatus('error');
        setCloudSaveError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Failed to save to cloud:', error);
      const errorMessage = error.message || 'Unknown error saving to cloud';
      setCloudSaveStatus('error');
      setCloudSaveError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [userId, properties, assumptions, investmentGoal, cloudSaveStatus]);

  // ========================================================================
  // Phase 1 follow-up: exponential backoff for transient sync failures.
  // Schedules a retry at 3s / 9s / 27s. After 3 attempts, gives up and
  // surfaces the manual "Sync failed — retry" button on the header.
  // Defined BEFORE _syncToCloudSilent to avoid TDZ. Uses syncFnRef to call
  // the latest _syncToCloudSilent (set by a useEffect below).
  // ========================================================================
  const scheduleRetry = useCallback(() => {
    if (retryCountRef.current >= RETRY_DELAYS_MS.length) {
      console.warn('[PortfolioDataContext] Auto-sync exhausted retries, waiting for manual retry');
      return;
    }
    const delay = RETRY_DELAYS_MS[retryCountRef.current];
    retryCountRef.current += 1;

    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      // Skip if a fresher edit is already pending — it'll sync soon enough
      if (autoSyncTimeoutRef.current) return;
      const ctrl = new AbortController();
      autoSyncAbortRef.current = ctrl;
      setCloudSaveStatus('syncing');
      const fn = syncFnRef.current;
      if (fn) {
        fn(ctrl.signal).finally(() => {
          if (autoSyncAbortRef.current === ctrl) autoSyncAbortRef.current = null;
        });
      }
    }, delay);
  }, [RETRY_DELAYS_MS]);

  // ========================================================================
  // Phase 1: Silent debounced auto-sync to backend.
  // Triggered by the auto-sync effect below; uses POST /profile/{userId}
  // full upsert (not just patchPortfolioData) so the rich investor profile
  // is also persisted. Status transitions: idle → syncing → synced → idle.
  // Supports AbortController so in-flight saves can be cancelled by newer edits.
  // ========================================================================
  const _syncToCloudSilent = useCallback(async (signal) => {
    try {
      // Build the portfolio_data payload (same shape as saveToCloud uses)
      const totalValue = properties.reduce((sum, p) =>
        sum + (p.value ?? p.currentValue ?? p.current_value ?? 0), 0
      );
      const totalDebt = properties.reduce((sum, p) =>
        sum + (p.loanAmount ?? p.loan_amount ?? p.loan ?? 0), 0
      );
      const totalEquity = totalValue - totalDebt;

      const portfolioData = {
        properties: properties.map(p => ({
          id: p.id || p._id,
          address: p.address || p.fullAddress,
          suburb: p.suburb,
          state: p.state,
          postcode: p.postcode,
          property_type: p.propertyType || p.property_type || p.type,
          purchase_price: p.purchasePrice ?? p.purchase_price,
          current_value: p.value ?? p.currentValue ?? p.current_value,
          weekly_rent: p.weeklyRent ?? p.weekly_rent ?? p.rent,
          loan_amount: p.loanAmount ?? p.loan_amount ?? p.loan,
          settlement_date: p.purchaseDate ?? p.purchase_date ?? p.settlementDate,
          purchase_year: p.purchaseYear ?? p.purchase_year ?? null,
          // 2026 budget reform — gates NG eligibility + CGT method per property.
          // Sent in both shapes the engine accepts (snake_case is canonical).
          contract_date: p.contract_date ?? p.contractDate ?? null,
          property_classification: p.property_classification ?? p.propertyClassification ?? null,
          // ADR-022 §4.2 — ATO Table 2 new build qualification (factsheet p.6).
          new_build_supply_basis: p.new_build_supply_basis ?? p.newBuildSupplyBasis ?? null,
          prior_ownership: p.prior_ownership ?? p.priorOwnership ?? null,
          // ADR-022 §4.4 — affordable housing 60% CGT discount preserved.
          affordable_housing_qualifying: p.affordable_housing_qualifying ?? p.affordableHousingQualifying ?? null,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          expenses: p.expenses,
          qsData: p.qsData,
          rentalData: p.rentalData,
          costBase: p.costBase,
          // PPOR flag — both casings for backend/engine parity. The
          // PropertyEditModal toggle wrote this into FE state but the
          // earlier auto-sync omitted it from the payload, so saves
          // silently stripped the flag before reaching the DB.
          property_category: p.propertyCategory ?? p.property_category ?? 'investment',
          propertyCategory: p.propertyCategory ?? p.property_category ?? 'investment',
          // Per-property ownership entity (2026-08-28) — labeling/display only.
          // No engine consumer reads it; the tax-surface partition is a
          // separate, gated phase. null = not set (treated as personal).
          ownership: p.ownership ?? null,
          // Backend-injected fields — round-trip so they survive saves
          weeklyRent: p.weeklyRent,
          lastStatementDate: p.lastStatementDate,
          annualInsurancePremium: p.annualInsurancePremium,
          insurancePolicyExpiry: p.insurancePolicyExpiry,
          lastInsuranceRenewalDate: p.lastInsuranceRenewalDate,
        })),
        totalValue,
        totalEquity,
        totalDebt,
        propertyCount: properties.length,
        assumptions,
        investmentGoal,
        lastUpdated: new Date().toISOString(),
      };

      // Build the full profile upsert payload
      const fullProfilePayload = {
        entity_type: investorProfile?.investmentEntity?.type || null,
        risk_profile: investorProfile?.riskProfile || null,
        profile_data: investorProfile || {},
        portfolio_data: portfolioData,
        strategy_data: portfolioStrategy || {},
        preferences_data: {
          geographicPreferences: investorProfile?.geographicPreferences || {},
          propertyFeatures: investorProfile?.propertyFeatures ?? {},
        },
      };

      // POST full upsert to /api/v1/investor/profile/{userId}
      // Uses upsertFullProfile (not updateInvestorProfile) because the latter
      // runs through _transformProfileToApi which would drop our new fields.
      const result = await investorProfileAPI.upsertFullProfile(userId, fullProfilePayload);

      // Check abort signal before applying state changes
      if (signal?.aborted) return { success: false, aborted: true };

      if (result?.success) {
        const savedAt = result.savedAt || new Date().toISOString();
        setLastCloudSave(savedAt);
        setCloudSaveStatus('synced');
        setCloudSaveError(null);
        // A success while a failure episode is open IS the recovery — pair it
        // with sync_failed so a blip that healed is distinguishable from data
        // loss in PostHog (fires once per episode, like its counterpart).
        if (retryCountRef.current > 0) {
          track.syncRecovered(lastSyncFailureStatusRef.current, retryCountRef.current);
          lastSyncFailureStatusRef.current = null;
        }
        // Reset retry counter on success — next failure starts fresh at 3s
        retryCountRef.current = 0;
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }

        // Auto-reset to idle after 3s
        if (cloudSaveTimeoutRef.current) {
          clearTimeout(cloudSaveTimeoutRef.current);
        }
        cloudSaveTimeoutRef.current = setTimeout(() => {
          setCloudSaveStatus(prev => prev === 'synced' ? 'idle' : prev);
          cloudSaveTimeoutRef.current = null;
        }, 3000);

        return { success: true, savedAt };
      }

      const errorMessage = result?.error || 'Auto-sync failed';
      setCloudSaveStatus('error');
      setCloudSaveError(errorMessage);
      // Make the failure visible — there's no prod telemetry on syncs otherwise.
      // Fire once per failure episode (retryCount 0), not on every backoff attempt.
      if (retryCountRef.current === 0) {
        lastSyncFailureStatusRef.current = result?.status ?? null;
        track.syncFailed(result?.status ?? null, result?.transient ?? null);
      }
      // Only auto-retry TRANSIENT failures; permanent ones (auth/validation) just
      // fail again, so surface the manual retry immediately instead of looping.
      if (result?.transient !== false) scheduleRetry();
      return { success: false, error: errorMessage };
    } catch (error) {
      if (error.name === 'AbortError' || signal?.aborted) {
        return { success: false, aborted: true };
      }
      console.warn('[PortfolioDataContext] Auto-sync failed:', error.message);
      setCloudSaveStatus('error');
      setCloudSaveError(error.message || 'Unknown auto-sync error');
      scheduleRetry();
      return { success: false, error: error.message };
    }
  }, [userId, properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, scheduleRetry]);

  /**
   * Flush any pending debounced sync IMMEDIATELY and return a promise that
   * resolves when the Supabase write completes (or rejects on hard error).
   *
   * Use this from flows that need a guaranteed durable save before navigating
   * (e.g. Strategy lock-in: setInvestmentGoal → flushPendingSave → navigate).
   * Without it, the 500ms debounce window can swallow the save if the user
   * leaves the page or refreshes immediately after locking in.
   */
  const flushPendingSave = useCallback(async () => {
    if (!userId) return { success: false, error: 'Not signed in' };
    // Cancel pending debounce so we don't double-fire
    if (autoSyncTimeoutRef.current) {
      clearTimeout(autoSyncTimeoutRef.current);
      autoSyncTimeoutRef.current = null;
    }
    // Cancel any in-flight sync to use a fresh AbortController
    if (autoSyncAbortRef.current) {
      autoSyncAbortRef.current.abort();
    }
    const ctrl = new AbortController();
    autoSyncAbortRef.current = ctrl;
    setCloudSaveStatus('syncing');
    return _syncToCloudSilent(ctrl.signal);
  }, [userId, _syncToCloudSilent]);

  // Keep syncFnRef pointing at the latest _syncToCloudSilent so scheduleRetry
  // can invoke it without creating a useCallback circular dep.
  useEffect(() => {
    syncFnRef.current = _syncToCloudSilent;
  }, [_syncToCloudSilent]);

  // ========================================================================
  // Phase 1: Debounced auto-sync effect.
  // Watches all syncable state and triggers _syncToCloudSilent 500ms after
  // the last change (matching AgentTab's existing pattern at SettingsPage:986).
  // Guards: skip during hydration, skip the post-reconcile echo, abort
  // in-flight saves on new edits.
  // ========================================================================
  useEffect(() => {
    // Don't auto-sync until reconciliation has finished
    if (!isLoaded || !reconcileCompleteRef.current) return;
    // Defensive: also gate on userId so a transient Clerk reload doesn't fire blank PUT
    if (!userId) return;

    // Skip the next sync if we just rehydrated from cloud (avoid echo)
    if (skipNextAutoSyncRef.current) {
      skipNextAutoSyncRef.current = false;
      return;
    }

    // Don't push empty defaults over a populated backend on first load.
    // IMPORTANT: if lastModified is set the user has made an explicit change
    // (including deleting all properties) — always sync in that case.
    if (!lastModified && !profileIsMeaningful(investorProfile) && properties.length === 0) {
      return;
    }

    // Cancel any pending sync timer
    if (autoSyncTimeoutRef.current) {
      clearTimeout(autoSyncTimeoutRef.current);
    }

    // Cancel any in-flight sync
    if (autoSyncAbortRef.current) {
      autoSyncAbortRef.current.abort();
    }

    // Schedule new sync
    autoSyncTimeoutRef.current = setTimeout(() => {
      const ctrl = new AbortController();
      autoSyncAbortRef.current = ctrl;
      setCloudSaveStatus('syncing');
      _syncToCloudSilent(ctrl.signal).finally(() => {
        if (autoSyncAbortRef.current === ctrl) {
          autoSyncAbortRef.current = null;
        }
      });
    }, 500); // 500ms — matches AgentTab debounce in SettingsPage

    return () => {
      if (autoSyncTimeoutRef.current) {
        clearTimeout(autoSyncTimeoutRef.current);
      }
    };
  }, [properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, lastModified, isLoaded, _syncToCloudSilent]);

  // ========================================================================
  // Phase 5e (single-source-of-truth): cross-tab sync via BroadcastChannel.
  //
  // When THIS tab finishes a successful auto-sync, post a message so other
  // tabs for the same user re-fetch from backend. Without this, edits made
  // in tab A only appear in tab B after a page reload.
  //
  // Falls back to no-op if BroadcastChannel is unavailable (very rare in 2026).
  // ========================================================================
  const broadcastChannelRef = useRef(null);

  // Setup the channel + listener. Runs once per mount per userId.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined' || !userId) return undefined;

    const ch = new BroadcastChannel('heyward.investor-profile');
    broadcastChannelRef.current = ch;

    ch.onmessage = async (ev) => {
      const msg = ev?.data;
      if (!msg || msg.type !== 'profile-updated') return;
      if (msg.userId !== userId) return;
      // Skip if this tab originated the broadcast (BroadcastChannel doesn't
      // echo to the sender, but we double-guard with origin marker).
      if (msg.origin === broadcastOriginIdRef.current) return;

      // Re-fetch from backend and apply if newer than local. Mirrors
      // reconcileWithBackend's last-writer-wins logic but as a one-shot
      // (not gated on reconcileCompleteRef which is for first-mount only).
      try {
        const result = await investorProfileAPI.getInvestorProfile(userId, { skipMockFallback: true });
        const backendData = result?.data;  // transformed — legacy fields only
        const rawData = result?.rawData;   // raw backend shape — profile_data + portfolio_data intact
        if (result?.source !== 'api' || !backendData) return;
        // Use rawData.updated_at for last-writer-wins timestamp check
        const backendUpdatedAt = rawData?.updated_at || backendData.updated_at || backendData.lastUpdated;
        const localTime = lastModifiedRef.current ? new Date(lastModifiedRef.current).getTime() : 0;
        const backendTime = backendUpdatedAt ? new Date(backendUpdatedAt).getTime() : 0;
        if (backendTime <= localTime) return;

        // BUG FIX: Use rawData (full profile_data JSONB) so householdIncome,
        // householdExpenses, assets, and geographicPreferences are not lost.
        const normalizedProfile = normalizeInvestorProfile(rawData ?? backendData);
        setInvestorProfileState(normalizedProfile);
        const pd = rawData?.portfolio_data;
        if (pd) {
          if (Array.isArray(pd.properties) && pd.properties.length > 0) {
            console.log('[BroadcastChannel] setting', pd.properties.length, 'properties from cross-tab sync');
            setProperties(pd.properties);
          }
          if (pd.investmentGoal) setInvestmentGoalState(pd.investmentGoal);
          if (pd.assumptions) setAssumptions({ ...DEFAULT_ASSUMPTIONS, ...pd.assumptions });
        }
        const sd = rawData?.strategy_data;
        if (sd && Object.keys(sd).length > 0) {
          setPortfolioStrategyState({ ...DEFAULT_PORTFOLIO_STRATEGY, ...sd });
        }
        const savedAt = backendUpdatedAt || new Date().toISOString();
        setLastCloudSave(savedAt);
        // Don't auto-sync back — we just rehydrated FROM the cloud.
        skipNextAutoSyncRef.current = true;
      } catch (err) {
        console.warn('[PortfolioDataContext] Cross-tab reconcile failed:', err.message);
      }
    };

    return () => {
      ch.close();
      if (broadcastChannelRef.current === ch) {
        broadcastChannelRef.current = null;
      }
    };
  }, [userId]);

  // Post on successful auto-sync so other tabs for the same user know to refetch.
  const broadcastOriginIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Math.random())
  );
  useEffect(() => {
    if (cloudSaveStatus !== 'synced') return;
    const ch = broadcastChannelRef.current;
    if (!ch || !userId) return;
    try {
      ch.postMessage({
        type: 'profile-updated',
        userId,
        lastModified: lastCloudSave,
        origin: broadcastOriginIdRef.current,
      });
    } catch {
      // Best-effort — tab might be closing.
    }
  }, [cloudSaveStatus, userId, lastCloudSave]);

  // ========================================================================
  // Phase 1: beforeunload safety — flush pending sync via sendBeacon.
  // Catches users who Cmd-W or close the tab during a debounced wait.
  // ========================================================================
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (cloudSaveStatus !== 'syncing' && !autoSyncTimeoutRef.current) return;
      try {
        const totalValue = properties.reduce((sum, p) =>
          sum + (p.value ?? p.currentValue ?? p.current_value ?? 0), 0
        );
        const totalDebt = properties.reduce((sum, p) =>
          sum + (p.loanAmount ?? p.loan_amount ?? p.loan ?? 0), 0
        );
        const payload = {
          entity_type: investorProfile?.investmentEntity?.type || null,
          risk_profile: investorProfile?.riskProfile || null,
          profile_data: investorProfile || {},
          portfolio_data: {
            properties,
            totalValue,
            totalDebt,
            totalEquity: totalValue - totalDebt,
            propertyCount: properties.length,
            assumptions,
            investmentGoal,
            lastUpdated: new Date().toISOString(),
          },
          strategy_data: portfolioStrategy || {},
          preferences_data: {
            geographicPreferences: investorProfile?.geographicPreferences || {},
            propertyFeatures: investorProfile?.propertyFeatures ?? {},
          },
        };
        const baseUrl = import.meta.env.REACT_APP_API_URL || window.location.origin;
        const url = `${baseUrl}/api/v1/investor/profile/${userId}`;
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } catch (e) {
        // best-effort — page is unloading
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [userId, properties, assumptions, investorProfile, investmentGoal, portfolioStrategy, cloudSaveStatus]);

  /**
   * Sync properties from investor profile (API data)
   */
  const syncFromInvestorProfile = useCallback((profile) => {
    if (!profile) return;

    setInvestorProfileState(profile);

    // Extract properties from profile
    const profileProperties = profile.currentPortfolio?.properties || [];

    if (profileProperties.length > 0) {
      console.log('[syncFromInvestorProfile] setting', profileProperties.length, 'properties — CALLER:', new Error().stack?.split('\n')[2]?.trim());
      // Merge with existing local data (preserve expenses, contacts, etc.)
      setProperties(prev => {
        const existingById = prev.reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});

        return profileProperties.map(p => {
          const initialized = initializePropertyCostBase(p);
          const existing = existingById[p.id];

          // Merge: keep local extensions, update base data from profile
          return {
            ...initialized,
            // Preserve locally-added data
            expenses: existing?.expenses || initialized.expenses,
            contacts: existing?.contacts || [],
            documents: existing?.documents || [],
            maintenance: existing?.maintenance || [],
            qsData: existing?.qsData || null,
            rentalData: existing?.rentalData || null,
          };
        });
      });
    }
  }, []);

  /**
   * Get a single property by ID
   * Handles both 'id' and '_id' field names
   */
  const getProperty = useCallback((id) => {
    return properties.find(p => p.id === id || p._id === id) || null;
  }, [properties]);

  /**
   * Update a property (partial update)
   */
  const updateProperty = useCallback((id, updates) => {
    setProperties(prev => prev.map(p => {
      if (p.id === id) {
        return { ...p, ...updates, lastUpdated: new Date().toISOString() };
      }
      return p;
    }));
    markModified();
  }, [markModified]);

  /**
   * Update property expenses
   */
  const updatePropertyExpenses = useCallback((id, expenses) => {
    updateProperty(id, { expenses });
  }, [updateProperty]);

  /**
   * Update property contacts
   */
  const updatePropertyContacts = useCallback((id, contacts) => {
    updateProperty(id, { contacts });
  }, [updateProperty]);

  /**
   * Update property documents
   */
  const updatePropertyDocuments = useCallback((id, documents) => {
    updateProperty(id, { documents });
  }, [updateProperty]);

  /**
   * Update property maintenance tasks
   */
  const updatePropertyMaintenance = useCallback((id, maintenance) => {
    updateProperty(id, { maintenance });
  }, [updateProperty]);

  /**
   * Update property QS report data
   */
  const updatePropertyQSData = useCallback((id, qsData) => {
    updateProperty(id, { qsData });
  }, [updateProperty]);

  /**
   * Update property rental income data
   */
  const updatePropertyRentalData = useCallback((id, rentalData) => {
    updateProperty(id, { rentalData });
  }, [updateProperty]);

  /**
   * Update property cost base (for CGT)
   */
  const updatePropertyCostBase = useCallback((id, costBase) => {
    updateProperty(id, { costBase });
  }, [updateProperty]);

  /**
   * Update cash flow assumptions
   */
  const updateAssumptions = useCallback((newAssumptions) => {
    setAssumptions(prev => ({ ...prev, ...newAssumptions }));
    markModified();
  }, [markModified]);

  /**
   * Link PPOR loan amount to investment goal's pporBalance
   * Called when a PPOR property is added with a loan
   */
  const linkPPORToGoal = useCallback((loanAmount) => {
    // Only link if user has PPOR Payoff goal and loan amount is positive
    if (investmentGoal?.goalType === 'ppor_payoff' && loanAmount > 0) {
      setInvestmentGoalState(prev => ({
        ...prev,
        pporBalance: loanAmount,
      }));
      console.info(`[PortfolioDataContext] Linked PPOR mortgage ($${loanAmount.toLocaleString()}) to payoff goal`);
      return true;
    }
    return false;
  }, [investmentGoal]);

  /**
   * Add a new property
   */
  const addProperty = useCallback((property) => {
    // Validate property data and log any issues
    validateProperty(property, 'PortfolioDataContext.addProperty');

    // Normalize field names before initializing
    const normalized = normalizeProperty(property);

    const newProperty = {
      ...initializePropertyCostBase(normalized),
      id: normalized.id || `prop-${Date.now()}`,
      createdAt: new Date().toISOString(),
      expenses: {},
      contacts: [],
      documents: [],
      maintenance: [],
      qsData: null,
      rentalData: null,
    };

    // Auto-link PPOR loan to goal's pporBalance
    if (isPropertyPPOR(newProperty)) {
      const loanAmount = newProperty.loanAmount ?? newProperty.loan_amount ?? newProperty.loan ?? 0;
      if (loanAmount > 0) {
        linkPPORToGoal(loanAmount);
      }
    }

    setProperties(prev => [...prev, newProperty]);
    markModified();
    track.propertyAdded();
    return newProperty;
  }, [markModified, linkPPORToGoal]);

  /**
   * Remove a property
   */
  const removeProperty = useCallback((id) => {
    console.log('[removeProperty] deleting id:', id);
    setProperties(prev => {
      const next = prev.filter(p => p.id !== id);
      console.log('[removeProperty] before:', prev.map(p => p.id), '→ after:', next.map(p => p.id));
      return next;
    });
    markModified();
    track.propertyDeleted();
  }, [markModified]);

  /**
   * Set investment goal (full replace)
   * Normalizes goal to ensure all required fields are present
   * Also clears strategy progress to prevent stale resume data
   */
  const setInvestmentGoal = useCallback((goal) => {
    // Normalize goal to ensure consistent data structure
    const normalizedGoal = normalizeInvestmentGoal(goal);
    console.info('[PortfolioDataContext] Setting goal:', {
      goalType: normalizedGoal?.goalType,
      targetIncome: normalizedGoal?.targetIncome,
      yearsToFreedom: normalizedGoal?.yearsToFreedom,
      hasSimulation: !!normalizedGoal?.simulation,
      hasProjections: !!normalizedGoal?.projections,
    });
    setInvestmentGoalState(normalizedGoal);
    // Clear strategy progress when goal is saved to prevent stale resume state
    setStrategyProgressState(null);
    localStorage.removeItem(STRATEGY_STORAGE_KEY);
    markModified();
  }, [markModified]);

  /**
   * Update investment goal (partial update)
   */
  const updateInvestmentGoal = useCallback((updates) => {
    setInvestmentGoalState(prev => prev ? { ...prev, ...updates } : updates);
    markModified();
  }, [markModified]);

  /**
   * Clear investment goal
   */
  const clearInvestmentGoal = useCallback(() => {
    setInvestmentGoalState(null);
    markModified();
  }, [markModified]);

  // ==========================================================================
  // PROGRESSIVE ONBOARDING (Tiered Data Management)
  // ==========================================================================

  /**
   * Set onboarding progress (full replace)
   * Used when saving progress at end of a tier
   */
  const setOnboardingProgress = useCallback((progress) => {
    setOnboardingProgressState(progress);
    // Also persist to localStorage for resume capability
    if (progress) {
      try {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify({
          ...progress,
          savedAt: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('Failed to save onboarding progress:', error);
      }
    }
    markModified();
  }, [markModified]);

  /**
   * Update onboarding progress (partial update)
   * Used during onboarding flow to update individual fields
   */
  const updateOnboardingProgress = useCallback((updates) => {
    setOnboardingProgressState(prev => {
      const updated = prev ? { ...prev, ...updates } : updates;
      // Persist to localStorage
      try {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify({
          ...updated,
          savedAt: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('Failed to save onboarding progress:', error);
      }
      return updated;
    });
    markModified();
  }, [markModified]);

  /**
   * Clear onboarding progress
   */
  const clearOnboardingProgress = useCallback(() => {
    setOnboardingProgressState(null);
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    markModified();
  }, [markModified]);

  /**
   * Load saved onboarding progress from localStorage
   */
  const loadOnboardingProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setOnboardingProgressState(parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load onboarding progress:', error);
    }
    return null;
  }, []);

  /**
   * Get current onboarding tier
   */
  const getOnboardingTier = useCallback(() => {
    return onboardingProgress?.tier || null;
  }, [onboardingProgress]);

  /**
   * Check if onboarding is complete for a tier
   */
  const isOnboardingTierComplete = useCallback((tier) => {
    if (!onboardingProgress) return false;
    return onboardingProgress.completedTiers?.includes(tier) || false;
  }, [onboardingProgress]);

  /**
   * Convert onboarding data to investment goal format
   * Used when user completes onboarding and commits to goal
   */
  const commitOnboardingToGoal = useCallback(() => {
    if (!onboardingProgress) return false;

    const goal = {
      // Core goal data
      goalType: onboardingProgress.goalType,
      targetIncome: onboardingProgress.targetIncome,
      pporBalance: onboardingProgress.pporBalance,

      // Financial situation
      householdIncome: onboardingProgress.householdIncome,
      existingProperties: onboardingProgress.existingPropertyCount || 0,

      // From Tier 2
      timelineYears: onboardingProgress.timeline,
      riskProfile: onboardingProgress.riskProfile,
      lvrPreference: onboardingProgress.lvrPreference,

      // Strategy from recommendation
      recommendedStrategy: onboardingProgress.recommendedStrategy,

      // Custom assumptions from Tier 3
      customAssumptions: onboardingProgress.customAssumptions,

      // Metadata
      onboardingTier: onboardingProgress.tier,
      committedAt: new Date().toISOString(),
    };

    setInvestmentGoalState(goal);
    markModified();

    return true;
  }, [onboardingProgress, markModified]);

  // ==========================================================================
  // STRATEGY JOURNEY PROGRESS (Save/Resume for FreedomJourney)
  // ==========================================================================

  /**
   * Set strategy progress (full replace)
   * Used when saving progress at end of a step
   */
  const setStrategyProgress = useCallback((progress) => {
    setStrategyProgressState(progress);
    // Persist to localStorage for resume capability
    if (progress) {
      try {
        localStorage.setItem(STRATEGY_STORAGE_KEY, JSON.stringify({
          ...progress,
          savedAt: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('Failed to save strategy progress:', error);
      }
    }
    markModified();
  }, [markModified]);

  /**
   * Update strategy progress (partial update)
   * Used during journey flow to update individual fields
   */
  const updateStrategyProgress = useCallback((updates) => {
    setStrategyProgressState(prev => {
      const updated = prev ? { ...prev, ...updates } : updates;
      // Persist to localStorage
      try {
        localStorage.setItem(STRATEGY_STORAGE_KEY, JSON.stringify({
          ...updated,
          savedAt: new Date().toISOString(),
        }));
      } catch (error) {
        console.error('Failed to save strategy progress:', error);
      }
      return updated;
    });
    markModified();
  }, [markModified]);

  /**
   * Clear strategy progress
   */
  const clearStrategyProgress = useCallback(() => {
    setStrategyProgressState(null);
    localStorage.removeItem(STRATEGY_STORAGE_KEY);
    markModified();
  }, [markModified]);

  /**
   * Load saved strategy progress from localStorage
   * Returns the saved progress if exists
   */
  const loadStrategyProgress = useCallback(() => {
    try {
      const saved = localStorage.getItem(STRATEGY_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setStrategyProgressState(parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load strategy progress:', error);
    }
    return null;
  }, []);

  /**
   * Check if there's resumable strategy progress
   */
  const hasStrategyProgress = useMemo(() => {
    return strategyProgress !== null && strategyProgress.step !== undefined;
  }, [strategyProgress]);

  /**
   * Reset to demo data - DISABLED
   * Mock data should not be used in engine calculations.
   * Users should input their own property data.
   */
  const resetToDemo = useCallback(() => {
    console.warn('resetToDemo is disabled - mock data should not be used for calculations');
    // Clear instead of loading mock data
    setProperties([]);
    setAssumptions(DEFAULT_ASSUMPTIONS);
    setInvestorProfileState(null);
    setInvestmentGoalState(null);
    setPortfolioStrategyState(DEFAULT_PORTFOLIO_STRATEGY);
  }, []);

  /**
   * Clear all data
   */
  const clearAll = useCallback(() => {
    setProperties([]);
    setAssumptions(DEFAULT_ASSUMPTIONS);
    setInvestorProfileState(null);
    setInvestmentGoalState(null);
    setPortfolioStrategyState(DEFAULT_PORTFOLIO_STRATEGY);
    // Remove cache + legacy keys on logout
    localStorage.removeItem(storageKey(userId));   // write-through cache
    localStorage.removeItem(GOAL_STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_PREFIX);   // legacy unscoped key
  }, [userId]);

  /**
   * Set investor profile (persisted to localStorage)
   */
  const setInvestorProfile = useCallback((profile) => {
    // Phase 5b: warning-only schema validation. Surface violations to the dev console
    // so engineers see them in real time, but never block the write — Phase 5f flips
    // this to throw once we've verified all callers produce valid v7 shapes.
    if (import.meta.env.DEV && profile) {
      const validation = safeParseInvestorProfile(normalizeInvestorProfile(profile));
      if (!validation.success) {
        console.warn(
          '[PortfolioDataContext] setInvestorProfile schema violation (warning-only, will throw after Phase 5f):',
          validation.error?.issues || validation.error
        );
      }
    }
    setInvestorProfileState(profile);
    markModified();
  }, [markModified]);

  // ── Watchlist auto-repair (open-gaps 2026-07-13) ─────────────────────────
  // Legacy preferredSuburbs entries that predate write-time validation
  // ("Berwick", "Northcote VIC") never enter watched_suburbs — the backend
  // builder skips them and no listing alert can ever fire. This one-shot
  // heals them on ANY app open (previously only a Settings visit did), then
  // auto-sync persists and the profile-upsert route triggers the watch-list
  // rebuild. Unresolvable/ambiguous entries (e.g. "Aberdeen" NSW+TAS) are
  // left as-is; Settings renders those with a "Needs format" badge.
  // Gated on reconcileCompleteRef so we only ever repair backend truth, not
  // the instant-load cache — a cache-phase write could push stale data.
  const suburbRepairRanRef = useRef(false);
  useEffect(() => {
    if (suburbRepairRanRef.current) return;
    if (!reconcileCompleteRef.current) return;
    const current = investorProfile?.geographicPreferences?.preferredSuburbs;
    if (!Array.isArray(current) || current.length === 0) return;
    if (current.every((entry) => isValidPreferredFormat(entry))) {
      suburbRepairRanRef.current = true;
      return;
    }
    suburbRepairRanRef.current = true;
    let stale = false;
    import('../utils/preferredSuburbRepair')
      .then(({ repairPreferredSuburbs }) => {
        if (stale) {
          // Profile moved while the chunk loaded — re-arm so the next
          // profile tick retries against fresh state (module now cached).
          suburbRepairRanRef.current = false;
          return;
        }
        const { repaired, changed } = repairPreferredSuburbs(current);
        if (changed) {
          console.log('[PortfolioDataContext] Auto-repaired preferredSuburbs:', current, '→', repaired);
          setInvestorProfile({
            ...investorProfile,
            geographicPreferences: {
              ...(investorProfile.geographicPreferences || {}),
              preferredSuburbs: repaired,
            },
          });
        }
      })
      .catch((err) => {
        // Non-fatal: entries stay flagged in Settings; next session retries.
        console.warn('[PortfolioDataContext] preferredSuburbs repair skipped:', err);
      });
    return () => { stale = true; };
  }, [investorProfile, setInvestorProfile]);

  /**
   * Set portfolio strategy
   */
  const setPortfolioStrategy = useCallback((strategy) => {
    setPortfolioStrategyState(prev => ({
      ...DEFAULT_PORTFOLIO_STRATEGY,
      ...prev,
      ...strategy,
    }));
    markModified();
  }, [markModified]);

  /**
   * Update portfolio strategy (partial update)
   */
  const updatePortfolioStrategy = useCallback((updates) => {
    setPortfolioStrategyState(prev => ({
      ...prev,
      ...updates,
    }));
    markModified();
  }, [markModified]);

  /**
   * Computed portfolio metrics
   * Uses safe field accessors to handle inconsistent field naming
   */
  const portfolioMetrics = useMemo(() => {
    const isEmpty = properties.length === 0;

    if (isEmpty) {
      return {
        isEmpty: true,
        totalValue: 0,
        totalDebt: 0,
        totalEquity: 0,
        portfolioLVR: 0,
        totalWeeklyRent: 0,
        avgYield: 0,
        propertyCount: 0,
        totalAnnualRent: 0,
        avgLVR: 0,
      };
    }

    // Use nullish coalescing for safe field access across naming variants
    const totalValue = properties.reduce((sum, p) =>
      sum + (p.value ?? p.currentValue ?? p.current_value ?? 0), 0
    );
    const totalDebt = properties.reduce((sum, p) =>
      sum + (p.loanAmount ?? p.loan_amount ?? p.loan ?? 0), 0
    );
    const totalEquity = totalValue - totalDebt;
    const portfolioLVR = totalValue > 0 ? (totalDebt / totalValue) * 100 : 0;
    const totalWeeklyRent = properties.reduce((sum, p) =>
      sum + (p.weeklyRent ?? p.weekly_rent ?? p.rent ?? 0), 0
    );
    const totalAnnualRent = totalWeeklyRent * 52;
    const avgYield = totalValue > 0 ? (totalAnnualRent / totalValue) * 100 : 0;

    // Calculate average LVR across properties (not weighted)
    const propertiesWithLoans = properties.filter(p =>
      (p.loanAmount ?? p.loan_amount ?? p.loan ?? 0) > 0
    );
    const avgLVR = propertiesWithLoans.length > 0
      ? propertiesWithLoans.reduce((sum, p) => {
          const value = p.value ?? p.currentValue ?? p.current_value ?? 0;
          const loan = p.loanAmount ?? p.loan_amount ?? p.loan ?? 0;
          return sum + (value > 0 ? (loan / value) * 100 : 0);
        }, 0) / propertiesWithLoans.length
      : 0;

    return {
      isEmpty: false,
      totalValue,
      totalDebt,
      totalEquity,
      portfolioLVR,
      totalWeeklyRent,
      totalAnnualRent,
      avgYield,
      avgLVR,
      propertyCount: properties.length,
    };
  }, [properties]);

  const value = useMemo(() => ({
    // Data
    properties,
    assumptions,
    investorProfile,
    investmentGoal,
    portfolioStrategy,
    isLoaded,
    isProfileReconciled,
    portfolioMetrics,

    // Property getters
    getProperty,

    // Property setters
    updateProperty,
    updatePropertyExpenses,
    updatePropertyContacts,
    updatePropertyDocuments,
    updatePropertyMaintenance,
    updatePropertyQSData,
    updatePropertyRentalData,
    updatePropertyCostBase,
    addProperty,
    removeProperty,
    linkPPORToGoal,

    // Assumptions
    updateAssumptions,

    // Investment Goal
    setInvestmentGoal,
    updateInvestmentGoal,
    clearInvestmentGoal,

    // Progressive Onboarding
    onboardingProgress,
    setOnboardingProgress,
    updateOnboardingProgress,
    clearOnboardingProgress,
    loadOnboardingProgress,
    getOnboardingTier,
    isOnboardingTierComplete,
    commitOnboardingToGoal,

    // Strategy Journey Progress (Save/Resume)
    strategyProgress,
    setStrategyProgress,
    updateStrategyProgress,
    clearStrategyProgress,
    loadStrategyProgress,
    hasStrategyProgress,

    // Profile & Strategy
    setInvestorProfile,
    setPortfolioStrategy,
    updatePortfolioStrategy,
    syncFromInvestorProfile,

    // Cloud sync
    saveToCloud,
    flushPendingSave,
    hasUnsavedChanges,
    lastCloudSave,
    cloudSaveStatus,
    cloudSaveError,

    // Reset/Clear
    resetToDemo,
    clearAll,
  }), [
    // Data dependencies
    properties,
    assumptions,
    investorProfile,
    investmentGoal,
    portfolioStrategy,
    isLoaded,
    isProfileReconciled,
    portfolioMetrics,
    // Callback dependencies (stable due to useCallback)
    getProperty,
    updateProperty,
    updatePropertyExpenses,
    updatePropertyContacts,
    updatePropertyDocuments,
    updatePropertyMaintenance,
    updatePropertyQSData,
    updatePropertyRentalData,
    updatePropertyCostBase,
    addProperty,
    removeProperty,
    linkPPORToGoal,
    updateAssumptions,
    setInvestmentGoal,
    updateInvestmentGoal,
    clearInvestmentGoal,
    onboardingProgress,
    setOnboardingProgress,
    updateOnboardingProgress,
    clearOnboardingProgress,
    loadOnboardingProgress,
    getOnboardingTier,
    isOnboardingTierComplete,
    commitOnboardingToGoal,
    strategyProgress,
    setStrategyProgress,
    updateStrategyProgress,
    clearStrategyProgress,
    loadStrategyProgress,
    hasStrategyProgress,
    setInvestorProfile,
    setPortfolioStrategy,
    updatePortfolioStrategy,
    syncFromInvestorProfile,
    saveToCloud,
    flushPendingSave,
    hasUnsavedChanges,
    lastCloudSave,
    cloudSaveStatus,
    cloudSaveError,
    resetToDemo,
    clearAll,
  ]);

  return (
    <PortfolioDataContext.Provider value={value}>
      {children}
    </PortfolioDataContext.Provider>
  );
};

export default PortfolioDataContext;
